import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DriverGreetingHeader,
  LiveLocationBanner,
  DriverLocationMap,
  RouteOverview,
  CollectionSummary,
  AlertsList,
} from './components';
import {
  DriverGreeting,
  RouteOverview as RouteOverviewType,
  CollectionSummaryItem,
  DashboardAlert,
  timeOfDayGreeting,
} from './driverDashboardTypes';
import { useDeliveriesStore } from '../../../store/deliveriesStore';
import { useRoutesStore } from '../../../store/routesStore';
import { useAuth } from '../../../contexts/AuthContext';
import { formatRouteId } from '../../../utils/formatId';
import styles from './DriverDashboard.module.css';

const DriverDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const dashboard = useDeliveriesStore((s) => s.dashboard);
  const stops = useDeliveriesStore((s) => s.stops);
  const fetchDashboard = useDeliveriesStore((s) => s.fetchDashboard);
  const fetchMyStops = useDeliveriesStore((s) => s.fetchMyStops);
  const myRoute = useRoutesStore((s) => s.myDriverRoute);
  const fetchMyRoute = useRoutesStore((s) => s.fetchMyDriverRoute);
  const updateRouteStatus = useRoutesStore((s) => s.updateRouteStatus);

  const handleStartRoute = async () => {
    if (!myRoute) {
      navigate('/routes');
      return;
    }
    try {
      // Idempotent: PATCH is fine even if it's already IN_PROGRESS.
      if (myRoute.status !== 'IN_PROGRESS' && myRoute.status !== 'COMPLETED') {
        await updateRouteStatus(myRoute.id, 'IN_PROGRESS');
      }
    } catch (err) {
      // Surface but don't block navigation — driver can still see stops.
      console.error('Failed to start route', err);
    }
    navigate('/routes');
  };

  useEffect(() => {
    fetchDashboard();
    fetchMyStops();
    fetchMyRoute();
  }, [fetchDashboard, fetchMyStops, fetchMyRoute]);

  const greeting = useMemo<DriverGreeting>(() => ({
    greeting: timeOfDayGreeting(),
    driverName: user?.full_name || 'Driver',
    subtitle: 'Your shift briefing for today',
    vehicleAssigned: '🚚 —',
    shiftDate: new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }),
    territory: '—',
    status: 'Online',
  }), [user]);

  const routeOverview: RouteOverviewType = {
    routeId: myRoute ? formatRouteId(myRoute.id) : '—',
    date: `${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · Today's Route`,
    description: "Today's Route Overview",
    status: dashboard && dashboard.completed_stops + dashboard.failed_stops >= dashboard.total_stops && dashboard.total_stops > 0
      ? 'COMPLETED'
      : (dashboard && dashboard.completed_stops + dashboard.failed_stops > 0 ? 'IN PROGRESS' : 'READY TO START'),
    stats: [
      { id: 'stops', label: 'TOTAL STOPS', value: String(dashboard?.total_stops ?? 0).padStart(2, '0') },
      { id: 'completed', label: 'COMPLETED', value: String(dashboard?.completed_stops ?? 0).padStart(2, '0') },
      { id: 'remaining', label: 'REMAINING', value: String(dashboard?.remaining_stops ?? 0).padStart(2, '0') },
      { id: 'failed', label: 'FAILED', value: String(dashboard?.failed_stops ?? 0).padStart(2, '0') },
      { id: 'collected', label: 'COLLECTED TODAY', value: `$${Math.round(dashboard?.total_collected_today ?? 0)}` },
    ],
  };

  const collectionSummary: CollectionSummaryItem[] = [
    { id: 'cash', label: 'COLLECTED TODAY', value: `$${Math.round(dashboard?.total_collected_today ?? 0).toLocaleString()}`, variant: 'green' },
    { id: 'count', label: 'COLLECTIONS COUNT', value: String(dashboard?.collections_count_today ?? 0), variant: 'blue' },
    { id: 'failed', label: 'FAILED TODAY', value: String(dashboard?.failed_today ?? 0), variant: 'orange' },
    { id: 'remaining', label: 'STOPS REMAINING', value: String(dashboard?.remaining_stops ?? 0), variant: 'dark' },
  ];

  // Alerts derived from current stop set: failed stops surfaced as warnings.
  const dashboardAlerts: DashboardAlert[] = stops
    .filter((s) => s.status === 'failed')
    .slice(0, 3)
    .map((s) => ({
      id: `failed-${s.id}`,
      title: 'Failed Delivery',
      description: `Stop #${s.id} (${s.customer_name || `Order ${s.order_id}`}) failed: ${s.failure_reason || 'reason not recorded'}`,
      timeAgo: s.attempted_at ? new Date(s.attempted_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—',
      variant: 'error',
    }));

  return (
    <div className={styles.container}>
      <DriverGreetingHeader data={greeting} />
      <LiveLocationBanner />
      <DriverLocationMap height={300} />
      <RouteOverview data={routeOverview} onStartRoute={handleStartRoute} />
      <CollectionSummary items={collectionSummary} />
      <AlertsList alerts={dashboardAlerts} />
    </div>
  );
};

export const DriverDashboardPage: React.FC = () => <DriverDashboard />;

export { DriverDashboard };
export default DriverDashboard;
